import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import {
    GetRowIdParams,
    RowClickedEvent,
    RowGroupOpenedEvent,
} from 'ag-grid-community';
import {
    ACTIONS,
    TRACKABLE_EVENTS,
    trackEventBudget,
} from 'helpers/trackerHelpers';
import { ExcelExportConfig } from 'routes/BudgetModule/contexts/GridApiContext';
import { getColumns, getSideBar } from './colDefinition';
import { datesFromPayload } from './helper';
import { ApT, NAME_FIELD } from './types';
import ApOperationGrid from './Operations/ApOperationGrid';
import { useLocalStorageAGGridProps } from './hooks';
import AgGridReactExposingApi from '../../shared/AgGridReactExposingAPI';

const getRowId = (params: GetRowIdParams) => params.data.id;

const StyAPAEGridWrapper = styled('div', {
    name: 'StyAPAEGridWrapper',
})(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '2.5rem',
    '& .ag-header-cell': {
        borderColor: theme.palette.lightGrey,
        borderWidth: '1px',
        borderStyle: 'solid',
    },
    '& .ag-floating-filter-body': {
        alignItems: 'center',
    },
    '& .ag-header-group-cell': {
        borderColor: theme.palette.lightGrey,
        borderWidth: '1px',
        borderStyle: 'hidden hidden solid solid',
    },
    '& div.ag-cell': {
        fontSize: '11px',
        borderColor: theme.palette.lightGrey,
        borderWidth: '1px',
        borderStyle: 'solid',
    },
}));
type Props = {
    targetYear: number;
    data: ApT[];
    isAP?: boolean;
    excelExport?: ExcelExportConfig;
};
function APAEGrid({
    targetYear,
    data,
    isAP = false,
    excelExport,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const columns = useMemo(
        () =>
            getColumns(
                data.length > 0 ? datesFromPayload(data[0]) : [],
                t,
                isAP,
                targetYear,
            ),
        [targetYear, data, t, isAP],
    );

    const [columnDefs, extraProps] = useLocalStorageAGGridProps(
        `${targetYear}_AP_${isAP}`,
        getRowId,
        columns,
    );

    const detailCellRendererParams = () => ({ targetYear });

    // TODO: merge this with hookParams to trace events
    const onRowGroupOpened = (event: RowGroupOpenedEvent) =>
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetOverview.aps.unpackOperations,
            {
                action: event.expanded ? ACTIONS.open : ACTIONS.close,
                ap: event.data[NAME_FIELD],
            },
        );

    return (
        <StyAPAEGridWrapper className="ag-theme-material">
            <AgGridReactExposingApi
                domLayout="autoHeight"
                masterDetail
                detailRowAutoHeight
                rowHeight={36}
                headerHeight={40}
                pagination
                paginationPageSize={10}
                rowData={data}
                sideBar={getSideBar(t)}
                floatingFiltersHeight={40}
                components={{
                    textFloatingFilter: TextFloatingFilter,
                    numberFloatingFilter: TextFloatingFilter,
                    detailCellRenderer: ApOperationGrid,
                }}
                detailCellRenderer="detailCellRenderer"
                detailCellRendererParams={detailCellRendererParams}
                overlayNoRowsTemplate={t('viewAllAps.noData', {
                    ressource_type: isAP ? 'AP' : 'AE',
                })}
                onRowClicked={(event: RowClickedEvent) => {
                    event.node.setExpanded(!event.node.expanded);
                }}
                onRowGroupOpened={onRowGroupOpened}
                columnDefs={columnDefs}
                {...extraProps}
                excelExport={excelExport}
            />
        </StyAPAEGridWrapper>
    );
}

export default APAEGrid;
